import * as React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { BusinessUnitSelect } from "@/components/finance/BusinessUnitSelect";
import {
    BUAllocationEditor,
    AllocationValue,
    allocationsAreValid,
} from "@/components/finance/BUAllocationEditor";
import { DEFAULT_BUSINESS_UNIT, BusinessUnit } from "@/constants/businessUnits";
import { useVatRate, useActiveWithholdingRates } from "@/hooks/finance/useTaxSettings";
import { formatCurrency } from "@/lib/currency";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

type CategoryOption = {
    id: string;
    name: string;
};
type DepartmentOption = {
    id: string;
    name: string;
};

type BankTransaction = any;

type VatMode = "none" | "add" | "deduct";

type ManualCreateExpenseDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    transaction: BankTransaction | null;
    categories: CategoryOption[];
    departments: DepartmentOption[];
    isSubmitting?: boolean;
    bankAccountId?: string | null;
    bankName?: string | null;
    onSubmit: (payload: {
        reference_id?: string;
        payment_reference: string;
        amount: number;
        base_amount: number;
        total_amount: number;
        net_payment: number;
        vat_rate: number;
        vat_amount: number;
        withholding_rate: number;
        withholding_amount: number;
        currency: string;
        expense_date: string;
        payment_date: string;
        category_id: string;
        vendor_name: string;
        department_id: string;
        payment_method?: string;
        notes?: string;
        bank_account_id?: string | null;
        business_unit?: BusinessUnit;
        bu_allocations?: AllocationValue[];
    }) => Promise<void> | void;
};

const PAYMENT_METHOD_OPTIONS = [
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cash", label: "Cash" },
    { value: "check", label: "Check" },
    { value: "petty_cash", label: "Petty Cash" },
];

function getDefaultAmount(tx: any) {
    const raw = Number(tx?.transaction_amount ?? 0);
    return Number.isFinite(raw) ? Math.abs(raw).toFixed(2) : "0.00";
}
function formatAmountInput(value: string | number) {
    const str = String(value ?? "").replace(/,/g, "").trim();
    if (!str) return "";

    const [intPartRaw, decPartRaw] = str.split(".");
    const intPart = intPartRaw.replace(/\D/g, "");
    const decPart = decPartRaw?.replace(/\D/g, "").slice(0, 2);

    const formattedInt = intPart ? Number(intPart).toLocaleString("en-US") : "";

    if (decPartRaw !== undefined) {
        return `${formattedInt}.${decPart ?? ""}`;
    }

    return formattedInt;
}

function normalizeAmountInput(value: string) {
    const cleaned = value.replace(/,/g, "").replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length <= 2) return cleaned;
    return `${parts[0]}.${parts.slice(1).join("")}`;
}

function parseFormattedAmount(value: string) {
    return Number(value.replace(/,/g, ""));
}

function getDefaultCurrency(tx: any) {
    return tx?.transaction_amount_currency || "";
}

function getDefaultDate(tx: any) {
    const raw = tx?.transaction_date;
    if (!raw) return "";

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

function getDefaultNotes(tx: any) {
    const parts = [
        tx?.description_details,
        tx?.transaction_id_reference ? `Reference: ${tx.transaction_id_reference}` : null,
    ].filter(Boolean);

    return parts.join("\n");
}

function getDefaultReferenceId(tx: any) {
    return tx?.transaction_id_reference || "";
}

export function ManualCreateExpenseDialog({
    open,
    onOpenChange,
    transaction,
    categories,
    departments,
    isSubmitting = false,
    bankAccountId,
    bankName,
    onSubmit,
}: ManualCreateExpenseDialogProps) {
    const [amount, setAmount] = React.useState("");
    const [currency, setCurrency] = React.useState("");
    const [expenseDate, setExpenseDate] = React.useState("");
    const [paymentDate, setPaymentDate] = React.useState("");
    const [categoryId, setCategoryId] = React.useState("");
    const [notes, setNotes] = React.useState("");
    const [referenceId, setReferenceId] = React.useState("");
    const [paymentReference, setPaymentReference] = React.useState("");
    const [paymentMethod, setPaymentMethod] = React.useState("bank_transfer");
    const [departmentId, setDepartmentId] = React.useState("");
    const [vendorName, setVendorName] = React.useState("");
    const [splitMode, setSplitMode] = React.useState<"single" | "split">("single");
    const [businessUnit, setBusinessUnit] = React.useState<BusinessUnit>(DEFAULT_BUSINESS_UNIT);
    const [buAllocations, setBuAllocations] = React.useState<AllocationValue[]>([]);
    const [vatMode, setVatMode] = React.useState<VatMode>("none");
    const [withholdingApplicable, setWithholdingApplicable] = React.useState(false);
    const [selectedWithholdingRateId, setSelectedWithholdingRateId] = React.useState("");
    const [taxOpen, setTaxOpen] = React.useState(false);

    const { data: defaultVatRate = 15 } = useVatRate();
    const { data: withholdingRates = [] } = useActiveWithholdingRates();

    React.useEffect(() => {
        if (!transaction || !open) return;
        const txDate = getDefaultDate(transaction);
        const txRef = getDefaultReferenceId(transaction);
        setReferenceId(txRef);
        setPaymentReference(txRef);
        setAmount(formatAmountInput(getDefaultAmount(transaction)));
        setCurrency(getDefaultCurrency(transaction));
        setExpenseDate(txDate);
        setPaymentDate(txDate);
        setNotes(getDefaultNotes(transaction));
        setVendorName("");
        setCategoryId("");
        setPaymentMethod("bank_transfer");
        setDepartmentId("");
        setSplitMode("single");
        setBusinessUnit(DEFAULT_BUSINESS_UNIT);
        setBuAllocations([]);
        setVatMode("none");
        setWithholdingApplicable(false);
        setSelectedWithholdingRateId("");
    }, [transaction, open]);

    const paymentTotals = React.useMemo(() => {
        const entered = parseFormattedAmount(amount) || 0;
        const vatRateDecimal = (defaultVatRate || 0) / 100;
        let baseAmount: number;
        let vatAmount: number;
        let subtotal: number;
        switch (vatMode) {
            case "add":
                baseAmount = entered;
                vatAmount = baseAmount * vatRateDecimal;
                subtotal = baseAmount + vatAmount;
                break;
            case "deduct":
                baseAmount = entered / (1 + vatRateDecimal);
                vatAmount = entered - baseAmount;
                subtotal = entered;
                break;
            default:
                baseAmount = entered;
                vatAmount = 0;
                subtotal = entered;
        }
        const selectedRate = withholdingApplicable && selectedWithholdingRateId
            ? withholdingRates.find((r: any) => r.id === selectedWithholdingRateId)
            : null;
        const withholdingRate = selectedRate?.rate || 0;
        const withholdingAmount = baseAmount * (withholdingRate / 100);
        const netPayment = subtotal - withholdingAmount;
        return {
            baseAmount,
            vatRate: vatMode !== "none" ? defaultVatRate : 0,
            vatAmount,
            subtotal,
            withholdingRate,
            withholdingAmount,
            netPayment,
        };
    }, [amount, vatMode, defaultVatRate, withholdingApplicable, selectedWithholdingRateId, withholdingRates]);

    const allocationsValid = splitMode === "single" || allocationsAreValid(buAllocations);

    const handleSubmit = async () => {
        if (!transaction) return;
        if (!categoryId) return;
        if (!departmentId) return;
        if (!vendorName.trim()) return;
        if (!paymentReference.trim()) return;
        if (splitMode === "split" && !allocationsAreValid(buAllocations)) return;

        await onSubmit({
            reference_id: referenceId.trim() || undefined,
            payment_reference: paymentReference.trim(),
            amount: paymentTotals.baseAmount,
            base_amount: paymentTotals.baseAmount,
            total_amount: paymentTotals.subtotal,
            net_payment: paymentTotals.netPayment,
            vat_rate: paymentTotals.vatRate,
            vat_amount: paymentTotals.vatAmount,
            withholding_rate: paymentTotals.withholdingRate,
            withholding_amount: paymentTotals.withholdingAmount,
            currency: currency.trim(),
            expense_date: expenseDate,
            payment_date: paymentDate || expenseDate,
            category_id: categoryId,
            department_id: departmentId,
            vendor_name: vendorName.trim(),
            payment_method: paymentMethod,
            notes: notes.trim() || undefined,
            bank_account_id: bankAccountId ?? transaction?.bank_account_id ?? null,
            business_unit: splitMode === "single" ? businessUnit : undefined,
            bu_allocations: splitMode === "split" ? buAllocations : undefined,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[620px]">
                <DialogHeader>
                    <DialogTitle>Create expense from bank transaction</DialogTitle>
                </DialogHeader>

                <div className="grid gap-4 py-2">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="expense-amount">Amount</Label>
                            <Input
                                id="expense-amount"
                                type="text"
                                inputMode="decimal"
                                value={amount}
                                onChange={(e) => {
                                    const normalized = normalizeAmountInput(e.target.value);
                                    setAmount(formatAmountInput(normalized));
                                }}
                                placeholder="0.00"
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="expense-currency">Currency</Label>
                            <Input
                                id="expense-currency"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                placeholder="USD"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="expense-date">Expense date *</Label>
                            <Input
                                id="expense-date"
                                type="date"
                                value={expenseDate}
                                onChange={(e) => setExpenseDate(e.target.value)}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="payment-date">Payment date *</Label>
                            <Input
                                id="payment-date"
                                type="date"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="expense-vendor-name">Vendor Name *</Label>
                            <Input
                                id="expense-vendor-name"
                                value={vendorName}
                                onChange={(e) => setVendorName(e.target.value)}
                                placeholder="Vendor name"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="expense-reference-id">Document Reference</Label>
                            <Input
                                id="expense-reference-id"
                                value={referenceId}
                                onChange={(e) => setReferenceId(e.target.value)}
                                placeholder="Invoice / receipt #"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="payment-reference">Payment Reference *</Label>
                            <Input
                                id="payment-reference"
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                placeholder="Bank transfer ref"
                            />
                        </div>
                    </div>

                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Label>Paid From Account</Label>
                            <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm">
                                <div className="font-medium text-slate-900">
                                    {bankName || "Unknown bank"}
                                </div>
                                <div className="text-xs text-slate-500">
                                    {bankAccountId || "No bank account id"}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="grid gap-2">
                                <Label>Category</Label>
                                <Select value={categoryId} onValueChange={setCategoryId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72 overflow-y-auto">
                                        {categories.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid gap-2">
                                <Label>Department</Label>
                                <Select value={departmentId} onValueChange={setDepartmentId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select department" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72 overflow-y-auto">
                                        {departments.map((dept) => (
                                            <SelectItem key={dept.id} value={dept.id}>
                                                {dept.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid gap-2">
                                <Label>Payment Method</Label>
                                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select payment method" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PAYMENT_METHOD_OPTIONS.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-2">
                        <Label>Business Unit Assignment *</Label>
                        <RadioGroup
                            value={splitMode}
                            onValueChange={(v) => setSplitMode(v as "single" | "split")}
                            className="flex gap-4"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="single" id="bank-bu-single" />
                                <Label htmlFor="bank-bu-single" className="font-normal cursor-pointer text-sm">
                                    Single BU
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="split" id="bank-bu-split" />
                                <Label htmlFor="bank-bu-split" className="font-normal cursor-pointer text-sm">
                                    Split across BUs
                                </Label>
                            </div>
                        </RadioGroup>
                        {splitMode === "single" ? (
                            <BusinessUnitSelect
                                value={businessUnit}
                                onValueChange={(v) => setBusinessUnit(v)}
                            />
                        ) : (
                            <div className="rounded-md border p-3">
                                <BUAllocationEditor
                                    value={buAllocations}
                                    onChange={setBuAllocations}
                                    compact
                                />
                            </div>
                        )}
                    </div>

                    <Collapsible open={taxOpen} onOpenChange={setTaxOpen}>
                        <div className="rounded-md border">
                            <CollapsibleTrigger asChild>
                                <button
                                    type="button"
                                    className="flex w-full items-center justify-between p-3 text-left"
                                >
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium">VAT & Withholding</span>
                                        <span className="text-xs text-muted-foreground">
                                            {vatMode === "none" ? "No VAT" : vatMode === "add" ? `Add VAT ${defaultVatRate}%` : `Deduct VAT ${defaultVatRate}%`}
                                            {withholdingApplicable ? ` · WHT ${paymentTotals.withholdingRate}%` : ""}
                                            {" · Net "}{formatCurrency(paymentTotals.netPayment, currency || "ETB")}
                                        </span>
                                    </div>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${taxOpen ? "rotate-180" : ""}`} />
                                </button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-3 px-3 pb-3">
                                <div className="grid gap-2">
                                    <Label>VAT Treatment</Label>
                                    <RadioGroup
                                        value={vatMode}
                                        onValueChange={(v) => setVatMode(v as VatMode)}
                                        className="flex gap-4"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="none" id="vat-none" />
                                            <Label htmlFor="vat-none" className="font-normal cursor-pointer text-sm">No VAT</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="add" id="vat-add" />
                                            <Label htmlFor="vat-add" className="font-normal cursor-pointer text-sm">Add VAT ({defaultVatRate}%)</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="deduct" id="vat-deduct" />
                                            <Label htmlFor="vat-deduct" className="font-normal cursor-pointer text-sm">Deduct VAT ({defaultVatRate}%)</Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div className="grid grid-cols-2 gap-4 items-end">
                                    <div className="flex items-center justify-between gap-3">
                                        <Label htmlFor="wht-switch" className="text-sm">Withholding tax applies</Label>
                                        <Switch
                                            id="wht-switch"
                                            checked={withholdingApplicable}
                                            onCheckedChange={(c) => {
                                                setWithholdingApplicable(c);
                                                if (!c) setSelectedWithholdingRateId("");
                                            }}
                                        />
                                    </div>
                                    {withholdingApplicable && (
                                        <div className="grid gap-1">
                                            <Label>Withholding Rate</Label>
                                            <Select value={selectedWithholdingRateId} onValueChange={setSelectedWithholdingRateId}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select rate" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(withholdingRates as any[]).map((r) => (
                                                        <SelectItem key={r.id} value={r.id}>
                                                            {r.name ? `${r.name} (${r.rate}%)` : `${r.rate}%`}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs tabular-nums border-t pt-2">
                                    <div className="text-muted-foreground">Base amount</div>
                                    <div className="text-right">{formatCurrency(paymentTotals.baseAmount, currency || "ETB")}</div>
                                    <div className="text-muted-foreground">VAT ({paymentTotals.vatRate}%)</div>
                                    <div className="text-right">{formatCurrency(paymentTotals.vatAmount, currency || "ETB")}</div>
                                    <div className="text-muted-foreground">Subtotal</div>
                                    <div className="text-right">{formatCurrency(paymentTotals.subtotal, currency || "ETB")}</div>
                                    <div className="text-muted-foreground">Withholding ({paymentTotals.withholdingRate}%)</div>
                                    <div className="text-right">-{formatCurrency(paymentTotals.withholdingAmount, currency || "ETB")}</div>
                                    <div className="font-semibold">Net Paid</div>
                                    <div className="text-right font-semibold">{formatCurrency(paymentTotals.netPayment, currency || "ETB")}</div>
                                </div>
                            </CollapsibleContent>
                        </div>
                    </Collapsible>


                    <div className="grid gap-2">
                        <Label htmlFor="expense-notes">Description</Label>
                        <Textarea
                            id="expense-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            placeholder="Optional notes"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !categoryId || !departmentId || !vendorName.trim() || !paymentReference.trim() || !expenseDate || !paymentDate || (withholdingApplicable && !selectedWithholdingRateId) || !allocationsValid}
                    >
                        {isSubmitting ? "Creating..." : "Create expense"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}